import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';

import { db } from '@/firebase-config';
import {
  COMMUNICATION_HISTORY_LIMIT,
  communicationHistoryItemFromRecord,
  communicationHistoryRecipientFromRecord,
  mergeCommunicationHistory,
} from '@/features/email/communication-history-model';
import type {
  CommunicationHistoryItem,
  CommunicationHistoryRecipient,
} from '@/features/email/communication-types';
import { buildProjectSubcollectionPath, type ProjectScope } from '@/features/sync/project-scope';

import type { CommunicationHistoryClient } from '../contracts';

const FIRESTORE_IN_LIMIT = 30;

function docData(doc: QueryDocumentSnapshot<DocumentData>): Record<string, unknown> {
  return doc.data();
}

async function loadRecipientsByCommunicationId(
  scope: ProjectScope,
  communicationIds: string[],
): Promise<Map<string, CommunicationHistoryRecipient[]>> {
  const recipientsByCommunicationId = new Map<string, CommunicationHistoryRecipient[]>();
  if (communicationIds.length === 0) {
    return recipientsByCommunicationId;
  }

  const recipientsRef = collection(db, buildProjectSubcollectionPath(scope, 'communicationRecipients'));
  for (let index = 0; index < communicationIds.length; index += FIRESTORE_IN_LIMIT) {
    const ids = communicationIds.slice(index, index + FIRESTORE_IN_LIMIT);
    const recipientsQuery = query(recipientsRef, where('communicationId', 'in', ids));
    const snapshot = await getDocs(recipientsQuery);
    for (const doc of snapshot.docs) {
      const recipient = communicationHistoryRecipientFromRecord(doc.id, docData(doc));
      const existing = recipientsByCommunicationId.get(recipient.communicationId) ?? [];
      existing.push(recipient);
      recipientsByCommunicationId.set(recipient.communicationId, existing);
    }
  }

  return recipientsByCommunicationId;
}

export class FirebaseCommunicationHistoryClient implements CommunicationHistoryClient {
  async loadProjectCommunicationHistory(scope: ProjectScope): Promise<CommunicationHistoryItem[]> {
    const communicationsRef = collection(db, buildProjectSubcollectionPath(scope, 'communications'));
    const communicationsQuery = query(
      communicationsRef,
      orderBy('sentAt', 'desc'),
      limit(COMMUNICATION_HISTORY_LIMIT),
    );
    const snapshot = await getDocs(communicationsQuery);
    if (snapshot.empty) {
      return [];
    }

    const ids = snapshot.docs.map((doc) => doc.id);
    const recipientsByCommunicationId = await loadRecipientsByCommunicationId(scope, ids);
    const communications = snapshot.docs.map((doc) => communicationHistoryItemFromRecord(
      doc.id,
      docData(doc),
      recipientsByCommunicationId.get(doc.id) ?? [],
    ));
    return mergeCommunicationHistory(communications);
  }
}
